import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { analyticsChoice, chooseAnalytics, startAnalytics, track } from "@/lib/analytics";
import { projects } from "@/data/projects";

const ENGAGEMENT_THRESHOLD_MS = 20_000;

const STATIC_TITLES: Record<string, string> = {
  "/": "Karim Siala — Robotics & Physical AI Engineer",
  "/projects": "Selected work — Karim Siala",
};

export function Analytics() {
  const { pathname } = useLocation();
  const [choice, setChoice] = useState(analyticsChoice);
  const project = useMemo(() => projects.find(p => pathname === `/projects/${p.id}`), [pathname]);

  useEffect(() => {
    startAnalytics();
    const update = () => setChoice(analyticsChoice());
    window.addEventListener("analytics-choice", update);
    return () => window.removeEventListener("analytics-choice", update);
  }, []);

  useEffect(() => {
    const title = project ? `${project.title} — Karim Siala` : STATIC_TITLES[pathname];
    if (title) document.title = title;
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = `https://karimsiala.com${pathname === "/" ? "/" : pathname}`;
    window.scrollTo(0, 0);
  }, [pathname, project]);

  useEffect(() => {
    if (choice !== "accepted") return;
    // Dedicated route events work independently of GA's automatic history page views.
    track("portfolio_view", { page_path: pathname, ...(project ? { project_id: project.id, category: project.category } : {}) });
    if (!project) return;

    let visibleMs = 0;
    let visibleSince = document.visibilityState === "visible" ? Date.now() : null;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      timer = setTimeout(() => {
        track("project_engaged", { project_id: project.id, category: project.category, engagement_seconds: ENGAGEMENT_THRESHOLD_MS / 1000 });
      }, ENGAGEMENT_THRESHOLD_MS - visibleMs);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        visibleSince = Date.now();
        schedule();
      } else {
        clearTimeout(timer);
        if (visibleSince !== null) visibleMs += Date.now() - visibleSince;
      }
    };

    if (visibleSince !== null) schedule();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [pathname, choice, project]);

  if (choice) return null;
  return (
    <aside className="analytics-notice" aria-label="Analytics preference">
      <p>May I use analytics to learn which work interests visitors? <Link viewTransition to="/privacy">Privacy</Link></p>
      <div>
        <button type="button" onClick={() => chooseAnalytics(false)}>No thanks</button>
        <button type="button" className="primary" onClick={() => chooseAnalytics(true)}>Sure!</button>
      </div>
    </aside>
  );
}
